import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthorizationService } from '@nimmsta/ng-commons-lib/auth';
import { LoadingIndicatorComponent } from '@nimmsta/ng-commons-lib/loading-indicator';
import { HttpErrorResponse } from '@angular/common/http';
import dayjs, { Dayjs } from 'dayjs';
import { DashboardComponentInterface } from '../../../../models/dashboard';
import {
  JobResultAnalyticsDashboardService,
  AggregationResult,
  BucketValue,
  JobResultAnalyticsDataResponse,
  JobResultBucketedAverageResponse,
} from './job-result-analytics-dashboard.service';
import {
  AnalyticsMetricType,
  ChartType,
  DateSourceType,
  DEFAULT_JOB_RESULT_ANALYTICS_DASHBOARD_RANGE,
  GroupingMode,
  JobResultAnalyticsDashboardConfiguration,
} from './job-result-analytics-dashboard-configuration';
import { JobDashboardRange } from '../job-dashboard/job-dashboard-configuration';

@Component({
  selector: 'app-job-result-analytics-dashboard',
  templateUrl: './job-result-analytics-dashboard.component.html',
  styleUrls: ['./job-result-analytics-dashboard.component.scss'],
  imports: [CommonModule, RouterLink, LoadingIndicatorComponent, TranslatePipe],
})
export class JobResultAnalyticsDashboardComponent
  implements OnInit, OnDestroy, DashboardComponentInterface
{
  private authorizationService = inject(AuthorizationService);
  private service = inject(JobResultAnalyticsDashboardService);
  private translate = inject(TranslateService);
  @Input() configuration!: JobResultAnalyticsDashboardConfiguration;

  intervalTime = 600000; // 10 minutes for analytics
  protected data: AggregationResult[] = [];
  protected bucketedAverage?: JobResultBucketedAverageResponse;
  protected totalJobCount = 0;
  protected warnings: string[] = [];
  protected error: string | null = null;
  protected isLoading = false;
  protected maxValue = 0;
  protected bucketMaxValue = 0;
  protected pieTotal = 0;
  protected pieAggregationLabel = '';
  protected pieChartBackground = 'conic-gradient(#e9ecef 0% 100%)';
  protected pieSegments: Array<{
    groupKey: string;
    value: number;
    percentage: number;
    color: string;
  }> = [];
  protected dateFrom?: Dayjs;
  protected dateTo?: Dayjs;
  private interval?: ReturnType<typeof setInterval>;
  private isFirstLoad = true;

  protected canNavigateToJobs =
    this.authorizationService.hasPermission('job.read');

  ChartType = ChartType;
  AnalyticsMetricType = AnalyticsMetricType;

  private readonly defaultConfig: Partial<JobResultAnalyticsDashboardConfiguration> =
    {
      range: DEFAULT_JOB_RESULT_ANALYTICS_DASHBOARD_RANGE,
      chartType: ChartType.TABLE,
      metricType: AnalyticsMetricType.AGGREGATE,
      aggregations: [],
      groupingMode: GroupingMode.NONE,
    };

  ngOnInit() {
    this.interval = setInterval(() => this.loadData(), this.intervalTime);
  }

  ngOnDestroy() {
    clearInterval(this.interval);
  }

  setConfiguration(configuration: unknown): void {
    this.configuration = {
      ...this.defaultConfig,
      ...this.configuration,
      ...(configuration as object),
    };
  }

  loadData() {
    if (
      this.isBucketedAverageConfiguration() &&
      !this.hasBucketedAverageMetricConfiguration()
    ) {
      this.resetAnalyticsState();
      this.isLoading = false;
      return;
    }

    if (
      !this.isBucketedAverageConfiguration() &&
      (!this.configuration.aggregations ||
        this.configuration.aggregations.length === 0)
    ) {
      this.resetAnalyticsState();
      this.isLoading = false;
      return;
    }

    // Compute the date range used
    if (this.configuration.range === JobDashboardRange.CUSTOM) {
      this.dateFrom = this.configuration.from
        ? dayjs(this.configuration.from)
        : undefined;
      this.dateTo = this.configuration.to
        ? dayjs(this.configuration.to)
        : undefined;
    } else {
      const now = dayjs();
      this.dateFrom = this.service.getFromForRange(
        this.configuration.range,
        now,
      );
      this.dateTo = this.service.getToForRange(this.configuration.range, now);
    }

    if (this.isFirstLoad) {
      this.isFirstLoad = false;
      this.isLoading = true;
    }
    this.service.getData(this.configuration).subscribe({
      next: (response) => {
        if (this.isBucketedAverageResponse(response)) {
          this.bucketedAverage = response;
          this.data = [];
          this.totalJobCount = 0;
          this.warnings = response.warnings || [];
        } else {
          this.bucketedAverage = undefined;
          this.data = response.results;
          this.totalJobCount = response.totalJobCount;
          this.warnings = response.warnings;
        }
        this.error = null;
        this.calculateMetrics();
        this.isLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        const errorMessage = this.getErrorMessage(err);
        this.resetAnalyticsState();
        this.error = errorMessage;
        this.isLoading = false;
      },
    });
  }

  private resetAnalyticsState(): void {
    this.data = [];
    this.bucketedAverage = undefined;
    this.totalJobCount = 0;
    this.warnings = [];
    this.error = null;
    this.maxValue = 0;
    this.bucketMaxValue = 0;
    this.pieTotal = 0;
    this.pieAggregationLabel = '';
    this.pieChartBackground = 'conic-gradient(#e9ecef 0% 100%)';
    this.pieSegments = [];
  }

  private calculateMetrics() {
    // Calculate max value for chart scaling
    const allValues = this.data.flatMap((r) =>
      Object.values(r.aggregations).map((v) => Number(v) || 0),
    );
    this.maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;
    const bucketValues =
      this.bucketedAverage?.buckets?.map(
        (bucket) => Number(bucket.value) || 0,
      ) || [];
    this.bucketMaxValue =
      bucketValues.length > 0 ? Math.max(...bucketValues) : 0;
    this.calculatePieChartData();
  }

  private calculatePieChartData(): void {
    const aggregationKey = this.getPrimaryAggregationKey();
    if (!aggregationKey) {
      this.pieTotal = 0;
      this.pieAggregationLabel = '';
      this.pieChartBackground = 'conic-gradient(#e9ecef 0% 100%)';
      this.pieSegments = [];
      return;
    }

    const values = this.data.map((row, index) => ({
      groupKey: row.groupKey,
      value: Number(row.aggregations[aggregationKey]) || 0,
      color: this.getPieChartColor(index),
    }));

    this.pieTotal = values.reduce((sum, entry) => sum + entry.value, 0);
    this.pieAggregationLabel = this.getAggregationLabel(aggregationKey);

    if (this.pieTotal <= 0) {
      this.pieSegments = values.map((entry) => ({
        ...entry,
        percentage: 0,
      }));
      this.pieChartBackground = 'conic-gradient(#e9ecef 0% 100%)';
      return;
    }

    let currentPercentage = 0;
    const gradientStops: string[] = [];
    this.pieSegments = values.map((entry) => {
      const percentage = (entry.value / this.pieTotal) * 100;
      const start = currentPercentage;
      currentPercentage += percentage;
      gradientStops.push(`${entry.color} ${start}% ${currentPercentage}%`);
      return { ...entry, percentage };
    });

    this.pieChartBackground = `conic-gradient(${gradientStops.join(', ')})`;
  }

  getBarWidthForRow(row: AggregationResult, key: string): number {
    const value = Number(row.aggregations[key]) || 0;
    const values = Object.values(row.aggregations).map((v) => Number(v) || 0);
    if (values.length <= 1) {
      if (this.maxValue === 0) return 0;
      return (value / this.maxValue) * 100;
    }
    const total = values.reduce((sum, current) => sum + current, 0);
    if (total === 0) return 0;
    return (value / total) * 100;
  }

  getBucketBarWidth(bucket: BucketValue): number {
    if (this.bucketMaxValue === 0) return 0;
    return ((Number(bucket.value) || 0) / this.bucketMaxValue) * 100;
  }

  formatValue(value: number): string {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value % 1 === 0 ? value.toString() : value.toFixed(2);
  }

  getAggregationKeys(): string[] {
    if (this.data.length === 0) return [];
    return Object.keys(this.data[0].aggregations);
  }

  private getPrimaryAggregationKey(): string | null {
    const firstRowWithAggregation = this.data.find(
      (row) => Object.keys(row.aggregations).length > 0,
    );
    if (!firstRowWithAggregation) {
      return null;
    }
    return Object.keys(firstRowWithAggregation.aggregations)[0] || null;
  }

  getAggregationLabel(key: string): string {
    // Convert "fieldKey_aggregationType" to "Field Key (Type)"
    const parts = key.split('_');
    const type = parts.pop()?.toUpperCase() || '';
    const field = parts.join('_');
    return `${field} (${type})`;
  }

  getPieChartColor(index: number): string {
    const colors = [
      '#4CAF50',
      '#2196F3',
      '#FF9800',
      '#E91E63',
      '#9C27B0',
      '#00BCD4',
      '#FFEB3B',
      '#795548',
    ];
    return colors[index % colors.length];
  }

  getDateRangeDisplay(): string {
    if (!this.dateFrom || !this.dateTo) {
      return '';
    }
    const fromStr = this.dateFrom.format('DD.MM.YYYY');
    const toStr = this.dateTo.format('DD.MM.YYYY');
    return `${fromStr} - ${toStr}`;
  }

  isCustomRange(): boolean {
    return this.configuration.range === JobDashboardRange.CUSTOM;
  }

  getRangeKey(): string {
    // Convert enum value to key for translation lookup
    const entries = Object.entries(JobDashboardRange);
    const entry = entries.find(
      ([, value]) => value === this.configuration.range,
    );
    return entry ? entry[0] : 'LAST_7_DAYS';
  }

  isDateGrouping(): boolean {
    return !!this.configuration?.dateGroupBy;
  }

  isBucketedAverageConfiguration(): boolean {
    return (
      this.configuration?.metricType === AnalyticsMetricType.BUCKETED_AVERAGE
    );
  }

  hasBucketedAverageMetricConfiguration(): boolean {
    const bucketedAverage = this.configuration?.bucketedAverage;
    if (!bucketedAverage?.value?.fieldKey?.trim()) {
      return false;
    }

    const dateSourceType =
      bucketedAverage.dateSource?.type || DateSourceType.JOB_LAST_UPDATED;
    return (
      dateSourceType !== DateSourceType.RESULT_FIELD ||
      !!bucketedAverage.dateSource?.fieldKey?.trim()
    );
  }

  getDateGranularityLabel(): string {
    const granularity = this.configuration?.dateGroupBy?.granularity;
    if (!granularity) {
      return '';
    }

    return this.translate.instant(
      `dashboard.edit.JobResultAnalyticsDashboardComponent.dateGranularityOptions.${granularity}`,
    );
  }

  getBucketGranularityLabel(): string {
    const granularity =
      this.bucketedAverage?.granularity ||
      this.configuration?.bucketedAverage?.bucket?.granularity;
    if (!granularity) {
      return '';
    }

    return this.translate.instant(
      `dashboard.edit.JobResultAnalyticsDashboardComponent.dateGranularityOptions.${granularity}`,
    );
  }

  getEmptyBucketModeLabel(): string {
    const emptyBucketMode =
      this.bucketedAverage?.emptyBucketMode ||
      this.configuration?.bucketedAverage?.emptyBucketMode;
    if (!emptyBucketMode) {
      return '';
    }

    return this.translate.instant(
      `dashboard.edit.JobResultAnalyticsDashboardComponent.emptyBucketModes.${emptyBucketMode}`,
    );
  }

  private isBucketedAverageResponse(
    response: JobResultAnalyticsDataResponse,
  ): response is JobResultBucketedAverageResponse {
    return 'averagePerBucket' in response;
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    // Handle specific error codes from the analytics endpoint
    if (err.status === 408) {
      return this.translate.instant(
        'dashboard.jobResultAnalytics.errors.timeout',
      );
    }
    if (err.status === 400) {
      // Try to get the message from the response, fall back to translation
      const message = err.error?.message || err.error?.error;
      if (message) {
        return message;
      }
      return this.translate.instant(
        'dashboard.jobResultAnalytics.errors.badRequest',
      );
    }
    // Generic error message
    return this.translate.instant(
      'dashboard.jobResultAnalytics.errors.generic',
    );
  }
}
